import { Box, Button, Flex, Kbd, Text, TextInput } from "@mantine/core";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ReasonTooltip } from "../ui/reason-tooltip";
import { PageHeader } from "../layout/Page";
import { Breadcrumb } from "../layout/breadcrumb";
import classes from "./list.module.css";

export type BreadcrumbItem = { label: string; to?: string };

type SearchProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
};

type Props = {
  breadcrumb?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  /** Small gray label above the title (e.g. «Нэхэмжлэх»). Prefer over breadcrumb when matching list spec. */
  eyebrow?: string;
  title: string;
  /** @deprecated Count moved to tab badges; kept for callers, not rendered. */
  count?: number;
  /** Optional muted line under title. */
  subtitle?: string;
  search?: SearchProps;
  primaryAction?: {
    label: string;
    onClick: () => void;
    leftSection?: ReactNode;
    /** Эрхгүй үед: товч нуугдахгүй, aria-disabled + энэ шалтгаан tooltip-д. */
    disabledReason?: string;
  };
  secondaryActions?: ReactNode;
  /** Right-side slot (e.g. stepper). Does not change list page callers. */
  extra?: ReactNode;
};

export function ListPageHeader({
  breadcrumb,
  breadcrumbs,
  eyebrow,
  title,
  subtitle,
  search,
  primaryAction,
  secondaryActions,
  extra,
}: Props) {
  const { t } = useTranslation("common");
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!search) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      searchRef.current?.focus();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [search]);

  const breadcrumbNode =
    eyebrow ? (
      <Text component="span" inherit>
        {eyebrow}
      </Text>
    ) : (
      breadcrumb ?? (breadcrumbs && breadcrumbs.length > 0 ? <Breadcrumb data={breadcrumbs} /> : null)
    );

  const hasActions = !!(primaryAction || secondaryActions || search || extra);

  const actions = hasActions ? (
    <Flex className={classes.pageHeaderActions}>
      {search && (
        <TextInput
          ref={searchRef}
          value={search.value}
          onChange={(e) => search.onChange(e.currentTarget.value)}
          placeholder={search.placeholder ?? t("search")}
          leftSection={<MagnifyingGlassIcon size={16} color="var(--mantine-color-gray-7)" />}
          leftSectionWidth={38}
          rightSection={<Kbd className={classes.headerKbd}>/</Kbd>}
          rightSectionWidth={40}
          className={classes.headerSearch}
        />
      )}
      {secondaryActions}
      {primaryAction && (
        <ReasonTooltip reason={primaryAction.disabledReason}>
          <Button
            className={classes.headerBtnPrimary}
            fw={600}
            leftSection={primaryAction.leftSection}
            onClick={primaryAction.onClick}
          >
            {primaryAction.label}
          </Button>
        </ReasonTooltip>
      )}
      {extra}
    </Flex>
  ) : undefined;

  return (
    <PageHeader
      breadcrumb={breadcrumbNode || undefined}
      title={title}
      subtitle={!eyebrow ? subtitle : undefined}
      extra={actions}
    />
  );
}
